import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { FlashMessageErrorHandlerService } from '../../services/flash-message-error-handler/flash-message-error-handler.service';
import { Subscription } from 'rxjs';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    imports: [NgbAlert, TranslateModule],
    selector: 'otb-error-flash-message',
    styleUrls: ['./error-flash-message.component.scss'],
    templateUrl: './error-flash-message.component.html',
    standalone: true,
})
export class ErrorFlashMessageComponent implements OnInit, OnDestroy {
    messages: Set<string> = new Set<string>();
    messageErrorCodeMap: { [key: string]: string } = {
        '400': 'dynamische_frontend_texte.error_message.400',
        '400_appointment-already-confirmed-exception':
            'dynamische_frontend_texte.error_message.400_appointment-already-confirmed-exception',
        '400_appointment-time-slot-already-booked-exception':
            'dynamische_frontend_texte.error_message.400_appointment-time-slot-already-booked-exception',
        '401': 'dynamische_frontend_texte.error_message.401',
        '403': 'dynamische_frontend_texte.error_message.403',
        '404': 'dynamische_frontend_texte.error_message.404',
        '500': 'dynamische_frontend_texte.error_message.500',
        '503': 'dynamische_frontend_texte.error_message.503',
    };
    show: boolean = false;
    subs: Subscription = new Subscription();
    constructor(
        private flashMessageErrorHandlerService: FlashMessageErrorHandlerService,
        private elementRef: ElementRef<HTMLElement>,
    ) {}

    ngOnInit(): void {
        const error$ = this.flashMessageErrorHandlerService.errorSubject.subscribe((error) => {
            if (error && error.status) {
                const message = this.mapErrorMessages(error);
                if (message) {
                    this.messages.add(message);
                    this.show = true;
                    this.focusCloseButton();
                }
            }
        });

        this.subs.add(error$);
    }

    private focusCloseButton(): void {
        setTimeout(() => {
            this.elementRef.nativeElement.querySelector<HTMLButtonElement>('button.btn-close')?.focus();
        });
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    close(): void {
        this.show = false;
        this.messages.clear();
        this.flashMessageErrorHandlerService.errorDismissed.next();
    }

    private mapErrorMessages(error: any): string {
        let message = '';
        if (error.status) {
            try {
                if (error.status === 400) {
                    message =
                        this.messageErrorCodeMap[this.generateErrorCode(error)] ?? this.messageErrorCodeMap['400'];
                } else {
                    message = this.messageErrorCodeMap[error.status] ?? this.fallbackMessage(error.status);
                }
            } catch (e) {
                console.error(e, 'No error message found for status code: ' + error.status);
                message = this.messageErrorCodeMap[error.status] ?? this.fallbackMessage(error.status);
            }
        }
        return message;
    }

    private fallbackMessage(status: number): string {
        return status >= 500 ? this.messageErrorCodeMap['500'] : this.messageErrorCodeMap['400'];
    }

    private generateErrorCode(error: any): string {
        // Angular parses JSON error bodies already, but keeps the raw string when parsing failed
        const jsonObject = typeof error.error === 'string' ? JSON.parse(error.error) : error.error;

        const exceptionName = jsonObject.errors[0].type
            .split('\\')
            .pop()
            .replace(/([a-z])([A-Z])/g, '$1-$2')
            .toLowerCase();

        return `${error.status}_${exceptionName}`;
    }
}
